/**
 * useObservationStatuses
 *
 * Hook que carga los status del módulo Observations dinámicamente desde
 *   GET /api/MasterData/statuses?statusGroup=observations
 *
 * Reemplaza las constantes hardcoded de
 * `src/utils/services/Observations/observationConstants.js`.
 *
 * Mantiene un fallback con los IDs originales para evitar que la UI rompa
 * en el primer render mientras llega la respuesta del backend.
 *
 * Devuelve la misma forma que las constantes anteriores (`OPEN`, `CLOSED`,
 * `DELETED`, `options`, `colors`) para minimizar cambios en los componentes
 * consumidores.
 */

import { useEffect, useMemo, useState } from "react";
import { getStatuses } from "../utils/services/control/masterDataService";

// IDs y keys de TAB_STATUSES grupo "observations".
// Se usan como fallback (1er render) y como referencia si el endpoint falla.
const FALLBACK_STATUSES = [
  { statusId: 23, statusKey: "open",    statusName: "Open" },
  { statusId: 24, statusKey: "closed",  statusName: "Closed" },
  { statusId: 25, statusKey: "deleted", statusName: "Deleted" },
];

// Colores de UI por key (no vienen del backend, son decisión de frontend).
const COLOR_BY_KEY = {
  open:    "green",
  closed:  "gray",
  deleted: "red",
};

export default function useObservationStatuses() {
  const [statuses, setStatuses] = useState(FALLBACK_STATUSES);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getStatuses("observations")
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setStatuses(data);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        // Mantener el fallback como statuses si el endpoint falla.
        console.warn("[useObservationStatuses] using fallback:", err?.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return useMemo(() => {
    const byKey = (k) => statuses.find((s) => s.statusKey === k);
    const open    = byKey("open");
    const closed  = byKey("closed");
    const deleted = byKey("deleted");

    // Options para Select/Dropdown — excluye `deleted` (no es seleccionable
    // por el usuario, solo se asigna desde el botón de borrar).
    const options = statuses
      .filter((s) => s.statusKey !== "deleted")
      .map((s) => ({
        id:    s.statusId,
        key:   s.statusKey,
        label: s.statusName,
      }));

    // Lookup de colores por id (mismo shape que el constante anterior).
    const colors = Object.fromEntries(
      statuses.map((s) => [s.statusId, COLOR_BY_KEY[s.statusKey] || "gray"])
    );

    return {
      // Estados
      loading,
      error,
      // IDs convenientes (mismo nombre que el objeto OBSERVATION_STATUS legacy)
      OPEN:    open?.statusId    ?? 23,
      CLOSED:  closed?.statusId  ?? 24,
      DELETED: deleted?.statusId ?? 25,
      // Lista cruda y derivados
      statuses,
      options,
      colors,
    };
  }, [statuses, loading, error]);
}
